<template>
  <div class="allocation-manage-container">
    <ds-page-header :title="$t('allocationStorehouseManage.page.title')" :subtitle="$t('allocationStorehouseManage.page.subtitle')">
      <template #extra>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="_openAllocationStorehouseApplyModal">
          {{ $t('allocationStorehouseManage.list.allocation') }}
        </el-button>
        <el-button size="small" icon="el-icon-download" @click="_exportExcel">
          {{ $t('common.export') }}
        </el-button>
      </template>
    </ds-page-header>

    <el-row :gutter="16">
      <el-col :xs="24" :sm="6" :md="5" :lg="4">
        <div class="ds-panel ds-panel--padded">
          <div class="ds-panel__header">
            <span class="ds-panel__title">{{ $t('allocationStorehouseManage.stateFilter') }}</span>
          </div>
          <ul class="ds-state-list">
            <li v-for="(item, index) in states" :key="index" @click="swatchState(item)"
              :class="{ 'is-active': conditions.state == item.statusCd }">
              {{ item.name }}
            </li>
          </ul>
        </div>
      </el-col>
      <el-col :xs="24" :sm="18" :md="19" :lg="20">
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.startUserName" clearable class="w-full"
                :placeholder="$t('allocationStorehouseManage.search.startUserName')" @keyup.enter.native="_queryAllocationStorehouseMethod" />
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.applyType" clearable class="w-full"
                :placeholder="$t('allocationStorehouseManage.search.applyType')">
                <el-option v-for="(item, index) in applyTypes" :key="index" :label="item.name" :value="item.statusCd" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="conditions.startTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
                :placeholder="$t('allocationStorehouseManage.search.startTime')" style="width:100%" />
            </el-col>
            <el-col v-show="moreCondition" :xs="24" :sm="12" :md="8" :lg="6">
              <el-date-picker v-model="conditions.endTime" type="datetime" value-format="yyyy-MM-dd HH:mm:ss"
                :placeholder="$t('allocationStorehouseManage.search.endTime')" style="width:100%" />
            </el-col>
          </el-row>
          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="_queryAllocationStorehouseMethod">{{ $t('common.search') }}</el-button>
            <el-button icon="el-icon-refresh-left" @click="_resetAllocationStorehouseMethod">{{ $t('common.reset') }}</el-button>
            <el-button type="text" @click="moreCondition = !moreCondition">{{ moreCondition ? $t('allocationStorehouseManage.hide') : $t('allocationStorehouseManage.more') }}</el-button>
          </template>
        </ds-search-panel>

        <div class="ds-panel">
          <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="_listAllocationStorehouses">
            <template #left><span class="ds-panel__title">{{ $t('allocationStorehouseManage.list.title') }}</span></template>
          </ds-toolbar>

          <ds-data-table :data="resourceStores" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('allocationStorehouseManage.page.empty')" @retry="_listAllocationStorehouses">
            <el-table-column prop="applyId" :label="$t('allocationStorehouseManage.table.applyId')" align="center" width="150">
              <template slot-scope="scope"><span class="ds-num">{{ scope.row.applyId || '-' }}</span></template>
            </el-table-column>
            <el-table-column prop="applyCount" :label="$t('allocationStorehouseManage.table.applyCount')" align="center" width="110">
              <template slot-scope="scope"><span class="ds-num">{{ scope.row.applyCount }}</span></template>
            </el-table-column>
            <el-table-column prop="startUserName" :label="$t('allocationStorehouseManage.table.startUserName')" align="center" min-width="100" />
            <el-table-column prop="stateName" :label="$t('allocationStorehouseManage.table.stateName')" align="center" width="110">
              <template slot-scope="scope">
                <ds-status-tag size="sm" :type="scope.row.state == '1200' ? 'warning' : scope.row.state == '1300' ? 'success' : 'muted'" :label="scope.row.stateName || '-'" />
              </template>
            </el-table-column>
            <el-table-column prop="applyTypeName" :label="$t('allocationStorehouseManage.table.applyTypeName')" align="center" width="110">
              <template slot-scope="scope"><ds-status-tag size="sm" type="info" :label="scope.row.applyTypeName || '-'" /></template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('allocationStorehouseManage.table.createTime')" align="center" width="170">
              <template slot-scope="scope"><span class="ds-num">{{ scope.row.createTime || '-' }}</span></template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="220" fixed="right">
              <template slot-scope="scope">
                <el-button v-if="scope.row.state == '1200' && currentUserId == scope.row.startUserId" type="danger" size="mini" @click="_openDeleteResourceStoreModel(scope.row)">
                  {{ $t('allocationStorehouseManage.operation.cancel') }}
                </el-button>
                <el-button type="primary" size="mini" @click="_toDetail(scope.row)">{{ $t('common.detail') }}</el-button>
                <el-button v-if="scope.row.applyType == '10000'" size="mini" @click="_openRunWorkflowImage(scope.row)">
                  {{ $t('allocationStorehouseManage.operation.flowChart') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :page="page.current" :size="page.size" :total="page.total"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </el-col>
    </el-row>

    <view-image ref="viewImage" />
    <allocation-storehouse ref="allocationStorehouse" />
    <delete-storehouse-manage ref="deleteStorehouseManage" @success="_listAllocationStorehouses" />
  </div>
</template>

<script>
import { getDict, getCommunityId } from '@/api/community/communityApi'
import { listAllocationStorehouseApplys, listStorehouses, exportData, listRunWorkflowImage } from '@/api/resource/allocationStorehouseManageApi'
import ViewImage from '@/components/system/viewImage'
import AllocationStorehouse from '@/components/resource/allocationStorehouse'
import DeleteStorehouseManage from '@/components/resource/deleteStorehouseManage'
import { getUserId } from '@/api/user/userApi'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'AllocationStorehouseManageList',
  components: { ViewImage, AllocationStorehouse, DeleteStorehouseManage, DsPageHeader, DsSearchPanel, DsToolbar, DsDataTable, DsPaginationBar, DsStatusTag },
  data() {
    return {
      loading: false, hasError: false, errorMessage: '', density: 'default', moreCondition: false,
      resourceStores: [], states: [], applyTypes: [], currentUserId: '',
      conditions: { applyId: '', resId: '', resName: '', resCode: '', shIda: '', shIdz: '', startTime: '', endTime: '', startUserId: '', startUserName: '', state: '', applyType: '', userId: '', communityId: '' },
      storehouses: [],
      page: { current: 1, size: 10, total: 0 }
    }
  },
  created() {
    this.currentUserId = getUserId()
    this.conditions.userId = this.currentUserId
    this.conditions.communityId = getCommunityId()
    this.initData()
  },
  methods: {
    async initData() {
      try {
        const [states, applyTypes] = await Promise.all([
          getDict('allocation_storehouse_apply', 'state'),
          getDict('allocation_storehouse_apply', 'apply_type')
        ])
        this.states = [{ statusCd: '', name: this.$t('common.all') }, ...states]
        this.applyTypes = applyTypes
        const storehouseRes = await listStorehouses({ page: 1, row: 100, communityId: this.conditions.communityId })
        this.storehouses = storehouseRes.data
        this._listAllocationStorehouses()
      } catch (error) { console.error('初始化数据失败:', error) }
    },
    async _listAllocationStorehouses() {
      try {
        this.loading = true; this.hasError = false
        const params = { ...this.conditions, page: this.page.current, row: this.page.size }
        const res = await listAllocationStorehouseApplys(params)
        this.resourceStores = res.data
        this.page.total = res.total
      } catch (error) { console.error('获取调拨记录失败:', error); this.hasError = true; this.errorMessage = this.$t('common.loadError') } finally { this.loading = false }
    },
    _queryAllocationStorehouseMethod() { this.page.current = 1; this._listAllocationStorehouses() },
    _resetAllocationStorehouseMethod() {
      const communityId = this.conditions.communityId
      this.conditions = { applyId: '', resId: '', resName: '', resCode: '', shIda: '', shIdz: '', startTime: '', endTime: '', startUserId: '', startUserName: '', state: '', applyType: '', userId: this.currentUserId, communityId }
      this.page.current = 1; this._listAllocationStorehouses()
    },
    _openDeleteResourceStoreModel(row) { this.$refs.deleteStorehouseManage.open(row) },
    _toDetail(item) {
      this.$router.push({ path: '/pages/common/allocationStorehouseDetail', query: { applyId: item.applyId, applyType: item.applyType, applyTypeName: item.applyTypeName } })
    },
    _openAllocationStorehouseApplyModal() { this.$router.push('/pages/common/allocationStorehouseApply') },
    async _openRunWorkflowImage(purchaseApply) {
      try {
        const res = await listRunWorkflowImage({ communityId: this.conditions.communityId, businessKey: purchaseApply.applyId })
        if (res.code !== '0') { this.$message.error(res.msg); return }
        this.$refs.viewImage.open({ url: 'data:image/png;base64,' + res.data })
      } catch (error) { console.error('获取流程图失败:', error) }
    },
    async _exportExcel() {
      try {
        const params = { ...this.conditions, pagePath: 'allocationStorehouseManage' }
        const res = await exportData(params)
        this.$message.success(res.msg)
        if (res.code === 0) { this.$router.push({ path: '/pages/property/downloadTempFile', query: { tab: this.$t('common.downloadCenter') } }) }
      } catch (error) { console.error('导出失败:', error) }
    },
    swatchState(item) { this.conditions.state = item.statusCd; this._listAllocationStorehouses() },
    handleSizeChange(val) { this.page.size = val; this.page.current = 1; this._listAllocationStorehouses() },
    handleCurrentChange(val) { this.page.current = val; this._listAllocationStorehouses() }
  }
}
</script>

<style lang="scss" scoped>
.allocation-manage-container { padding: var(--ds-spacing-lg); background-color: var(--ds-gray-100); min-height: calc(100vh - 60px); }
.ds-panel { background: #fff; border: 1px solid var(--ds-gray-300); border-radius: var(--ds-radius-md); box-shadow: var(--ds-shadow-sm); overflow: hidden; margin-bottom: var(--ds-spacing-lg);
  &--padded { padding: var(--ds-spacing-lg); }
  &__header { display: flex; align-items: center; justify-content: space-between; padding-bottom: var(--ds-spacing-md); margin-bottom: var(--ds-spacing-md); border-bottom: 1px solid var(--ds-gray-200); }
  &__title { font-size: var(--ds-font-size-lg); font-weight: 600; color: var(--ds-gray-800); }
}
.ds-state-list { list-style: none; margin: 0; padding: 0;
  li { padding: var(--ds-spacing-sm) var(--ds-spacing-md); font-size: var(--ds-font-size-sm); color: var(--ds-gray-600); border-radius: var(--ds-radius-sm); cursor: pointer; text-align: center; margin-bottom: var(--ds-spacing-xs);
    &:hover { background: var(--ds-gray-100); color: var(--ds-primary); }
    &.is-active { background: var(--ds-primary-sub); color: var(--ds-primary); font-weight: 600; } }
}
.w-full { width: 100%; }
</style>
